import React, { useState, useEffect, useRef } from 'react'
import DashboardContainer from 'containers/DashboardContainer'
import { useDispatch, useSelector } from 'react-redux'
import FirstStep from '../FirstStep/FirstStep'
import SelectFlyerStep from '../SelectFlyerStep/SelectFlyerStep'
import ThirdStep from '../ThirdStep/ThirdStep'
import DraftStep from '../DraftStep/DraftStep'
import EventSetting from '../EventSetting/EventSetting'
import HobnobModal from 'components/common/HobnobModal'
import cn from 'classnames'
import styles from './normal-create-event.scss'
import {
  fetchCreateEvent,
  fetchUploadFlyer,
  fetchUploadVideoFlyer,
  fetchMeWithCommunities,
  resetEventForm
} from 'src/actions/event'
import { fetchFlyerDesigns, setCustomFlyerURL, clearCustomFlyerURL } from 'src/actions/flyer'
import LoadingComponent from 'components/common/LoadingComponent/LoadingComponent'
import { routerActions } from 'react-router-redux'
import uuid from 'uuid'
import moment from 'moment'
import 'moment-timezone'

import BackIcon from 'src/assets/icons/back_button.png'
import SettingIcon from 'src/assets/icons/setting-icon.png'
import SearchIcon from 'src/assets/icons/ico-search-outlined.svg'
import CloseIcon from 'src/assets/icons/close-icon.png'
import ImagesIcon from 'src/assets/icons/icon_images.png'
import { redirectDomain } from 'services/url_helpers'
import CustomFlyerPicker from 'src/components/common/CustomFlyerPicker'
import { message } from 'antd'
import { showLogin } from 'src/actions/login'
import { planSelector, flyerSelector } from 'src/selectors'
import SvgIcon from 'src/components/common/SvgIcon'
import ImageSearchModal from '../ImageSearchModal/ImageSearchModal'

const stepList = [
  { title: 'Create Event', step: 1 },
  { title: 'Design', step: 2 },
  { title: 'Event Details', step: 3 }
]

const CreateEventContainer = () => {
  const dispatch = useDispatch()
  const eventForm = useSelector(state => state.frontend.eventForm)
  const changeEventFormStatus = useSelector(state => state.frontend.changeEventFormStatus)
  const { customFlyerURL } = useSelector(flyerSelector)
  const userId = useSelector(state => state.userId)
  const { visible: isPlanModalVisible } = useSelector(planSelector)

  const onNavigationList = () => dispatch(routerActions.push({ pathname: '/e/list' }))
  const onResetEventForm = () => dispatch(resetEventForm())
  const onRequireLogin = params => dispatch(showLogin(params))
  const onClearCustomFlyerURL = () => dispatch(clearCustomFlyerURL())

  const [currentStep, setCurrentStep] = useState(1)
  const [isOpenSetting, setIsOpenSetting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isImageSearchLoading, setIsImageSearchLoading] = useState(false)
  const [isOpenToolTip, setIsOpenToolTip] = useState(false)
  const [isOpenImageSearch, setIsOpenImageSearch] = useState(false)
  const secondStepRef = useRef(null)

  useEffect(() => {
    dispatch(fetchFlyerDesigns())
    dispatch(fetchMeWithCommunities())
    handleClearAndResetEventForm()
  }, [])

  useEffect(() => {
    if (isPlanModalVisible) {
      setIsOpenSetting(false)
    }
  }, [isPlanModalVisible])

  const handleClickBack = () => {
    if (currentStep === 1) {
      changeEventFormStatus === 'updated' ? setIsOpenToolTip(true) : onNavigationList()
    } else if (currentStep === 2) {
      setCurrentStep(1)
      setIsOpenSetting(false)
    } else if (currentStep === 3) {
      setCurrentStep(2)
      setIsOpenSetting(false)
    } else if (currentStep === 4) {
      setCurrentStep(3)
      setIsOpenSetting(false)
    }
  }

  const handleClearAndResetEventForm = () => {
    onClearCustomFlyerURL()
    onResetEventForm()
  }

  const handleRightButton = () => {
    if (eventForm.flyer_design_number) {
      handleClearAndResetEventForm()
      onNavigationList()
    } else {
      setCurrentStep(2)
    }
    setIsOpenToolTip(false)
  }

  const handleLeftButton = () => {
    setIsOpenToolTip(false)
    if (eventForm.flyer_design_number) {
      secondStepRef.current?.selectedFlyerRef?.canvasToImage()
      handleCreateEvent('save')
    } else {
      handleClearAndResetEventForm()
      onNavigationList()
    }
  }

  const handleCreateEvent = type => {
    if (userId) {
      handleCreateEventV2(type)
    } else {
      const params = {
        loginModalSubtitle: 'In order for you to create an Event, we need to confirm your number'
      }
      onRequireLogin(params)
    }
  }

  const handleCreateEventV2 = type => {
    const eventParams = { ...eventForm }

    delete eventParams.checkedFlyer

    if (eventParams.starts_at) {
      eventParams.starts_at = moment.tz(eventParams.starts_at, eventParams.timezone).format()
      if (eventParams.ends_at) {
        eventParams.ends_at = moment.tz(eventParams.ends_at, eventParams.timezone).format()
      }
    }

    if (eventForm.child_count_enabled) {
      eventParams.ticket_types_attributes = [
        {
          id: uuid.v4(),
          label: 'adult',
          price: 0
        },
        {
          id: uuid.v4(),
          label: 'child',
          price: 0
        }
      ]
    } else {
      eventParams.ticket_types_attributes = [
        {
          id: uuid.v4(),
          label: 'adult',
          price: 0
        }
      ]
    }

    setIsLoading(true)
    dispatch(
      fetchCreateEvent({
        eventForm: eventParams,
        onSuccessCallback: data => {
          if (eventForm.mediaType === 'video') {
            if (customFlyerURL) {
              handleUploadFlyer(data.event.id, data.event.url, type)
            } else {
              handleUploadVideoFlyer(data.event.id, data.event.url, type)
            }
          } else {
            handleUploadFlyer(data.event.id, data.event.url, type)
          }
        },
        onErrorCallback: () => {
          setIsLoading(false)
          message.error('Creation failed.')
        }
      })
    )
  }

  const handleUploadVideoFlyer = (eventId, eventUrl, type) => {
    dispatch(
      fetchUploadVideoFlyer({
        eventId,
        design_number: eventForm.flyer_design_number,
        imageBgFile: customFlyerURL,
        onSuccessCallback: async data => {
          const requests = []
          if (data?.video_flyer?.image_bg_upload?.presigned_url) {
            requests.push(
              fetch(data.video_flyer.image_bg_upload.presigned_url, {
                headers: {
                  'Content-Type': 'image/png'
                },
                method: 'PUT',
                body: eventForm.checkedFlyer
              })
            )
          }

          Promise.all(requests).then(responses => {
            if (responses.every(response => response.status === 200)) {
              handleClearAndResetEventForm()
              setTimeout(() => {
                if (type === 'save') {
                  location.href = window.location.origin + '/e/list'
                } else {
                  redirectDomain(eventUrl + '/')
                }
              }, 5000)
            } else {
              setIsLoading(false)
              alert('Upload Error.')
            }
          })
        },
        onErrorCallback: () => {
          setIsLoading(false)
          message.error('Creation failed.')
        }
      })
    )
  }

  const handleUploadFlyer = (eventId, eventUrl, type) => {
    dispatch(
      fetchUploadFlyer({
        eventId,
        design_number: eventForm.flyer_design_number,
        customBgDataURL: customFlyerURL,
        onSuccessCallback: async data => {
          const requests = []
          if (data?.image_flyer?.image_upload?.presigned_url) {
            requests.push(
              fetch(data.image_flyer.image_upload.presigned_url, {
                headers: {
                  'Content-Type': 'image/png'
                },
                method: 'PUT',
                body: eventForm.checkedFlyer
              })
            )
          }

          if (data?.image_flyer?.image_bg_upload?.presigned_url) {
            const flyerBgImage = await fetch(customFlyerURL)
            const flyerBgImageBlob = await flyerBgImage.blob()
            requests.push(
              fetch(data.image_flyer.image_bg_upload.presigned_url, {
                headers: {
                  'Content-Type': 'image/png'
                },
                method: 'PUT',
                body: flyerBgImageBlob
              })
            )
          }

          if (requests.length === 0) {
            setIsLoading(false)
            alert('Upload Error.')
            return
          }

          Promise.all(requests).then(responses => {
            if (responses.every(response => response.status === 200)) {
              handleClearAndResetEventForm()
              setTimeout(() => {
                if (type === 'save') {
                  location.href = window.location.origin + '/e/list'
                } else {
                  redirectDomain(eventUrl + '/')
                }
              }, 5000)
            } else {
              setIsLoading(false)
              alert('Upload Error.')
            }
          })
        },
        onErrorCallback: () => setIsLoading(false)
      })
    )
  }

  const renderHeaderLeft = () => {
    return (
      <img src={currentStep === 1 ? CloseIcon : BackIcon} onClick={handleClickBack} width="12" />
    )
  }

  const renderRightButtons = () => {
    if (currentStep === 2) {
      return (
        <section className={styles['action-container']}>
          <div className={styles['action-item']}>
            <CustomFlyerPicker>
              <img src={ImagesIcon} width={'24px'} />
            </CustomFlyerPicker>

            <SvgIcon
              className={styles['search-icon']}
              icon={SearchIcon}
              onClick={() => setIsOpenImageSearch(true)}
              width={24}
            />
          </div>
        </section>
      )
    } else if (currentStep === 3) {
      return <img src={SettingIcon} onClick={() => setIsOpenSetting(true)} width="20" />
    }
  }

  const renderToolTipText = () => {
    if (eventForm.flyer_design_number) {
      return {
        title: 'would you like to save your invite for later?',
        leftButton: 'Save Draft',
        rightButton: 'Delete Draft'
      }
    } else {
      return {
        title: 'Must Select Flyer to Save Draft',
        leftButton: "Don't Save",
        rightButton: 'Select flyer'
      }
    }
  }

  const handleImageSearchSave = async (url) => {
    setIsImageSearchLoading(true)

    try {
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache'
      })

      if (response.ok) {
        const blob = await response.blob()
        const reader = new FileReader()

        reader.onload = (e) => {
          dispatch(setCustomFlyerURL(e.target.result))
          setIsOpenImageSearch(false)
          setIsImageSearchLoading(false)
        }

        reader.onerror = () => {
          throw new Error('Failed to read blob')
        }

        reader.readAsDataURL(blob)
        return
      } else {
        throw new Error(`HTTP ${response.status}`)
      }
    } catch (fetchError) {
      console.warn('Fetch failed, trying Image method:', fetchError)

      try {
        await loadImageWithCanvas(url)
        setIsOpenImageSearch(false)
        setIsImageSearchLoading(false)
      } catch (imageError) {
        console.error('Both methods failed:', imageError)
        message.error('Failed to load image. Please try a different image.')
        setIsOpenImageSearch(false)
        setIsImageSearchLoading(false)
      }
    }
  }

  const loadImageWithCanvas = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')

          canvas.width = img.naturalWidth
          canvas.height = img.naturalHeight
          ctx.drawImage(img, 0, 0)

          const dataURL = canvas.toDataURL('image/jpeg', 0.8)
          dispatch(setCustomFlyerURL(dataURL))
          resolve(dataURL)
        } catch (error) {
          reject(error)
        }
      }

      img.onerror = () => {
        reject(new Error('Image load failed'))
      }

      img.src = url
    })
  }

  const tooltipText = renderToolTipText()
  const step = stepList.find(item => item.step === currentStep)

  return (
    <DashboardContainer
      title={stepList.find(item => item.step === currentStep)?.title}
      left={renderHeaderLeft()}
      right={renderRightButtons()}
      width={88}
    >
      <div className={styles['create-event-container']}>
        <div className={cn(styles['back-button'])} onClick={handleClickBack}>
          <img src={currentStep === 1 ? CloseIcon : BackIcon} width="12" />
        </div>

        <div className={styles['create-wrapper']}>
          <header className={cn(styles['create-header-wrapper'], currentStep === 2 && styles['image-search-header'])}>
            <h1>{step?.title}</h1>
            {
              currentStep === 2 && (
                <div
                  className={styles['image-search-label-wrapper']}
                  onClick={() => setIsOpenImageSearch(true)}
                >
                  <SvgIcon className={styles['search-icon']} icon={SearchIcon} />
                  <span>Image Search</span>
                </div>
              )
            }
            {currentStep === 3 && (
              <div
                className={styles['setting-label-wrapper']}
                onClick={() => setIsOpenSetting(true)}
              >
                <img src={SettingIcon} width="20" />
                <span>settings</span>
              </div>
            )}
          </header>

          <ul className={styles['progress-wrapper']}>
            {stepList.map(item => (
              <li
                key={item.step}
                className={cn(styles['progress-item'], {
                  [styles['progress-item-checked']]: currentStep >= item.step
                })}
              />
            ))}
          </ul>

          <div
            className={cn(styles['create-step-wrapper'], {
              [styles['create-step-hidden']]: currentStep !== 1
            })}
          >
            <FirstStep onButtonClick={() => setCurrentStep(2)} />
          </div>

          {currentStep === 2 && (
            <div className={styles['create-step-wrapper']}>
              <SelectFlyerStep
                onSecondStepRef={ref => (secondStepRef.current = ref)}
                onButtonClick={() => setCurrentStep(3)}
              />
            </div>
          )}

          <div
            className={cn(styles['create-step-wrapper'], {
              [styles['create-step-hidden']]: currentStep !== 3
            })}
          >
            <ThirdStep onCreateEvent={handleCreateEvent} />
          </div>
        </div>

        {currentStep === 4 && <DraftStep onClickBack={handleClickBack} />}

        {isOpenSetting && currentStep === 3 && (
          <EventSetting onClose={() => setIsOpenSetting(false)} />
        )}

        {
          isOpenImageSearch && currentStep === 2 && (
            <ImageSearchModal
              eventName={eventForm.name}
              show={isOpenImageSearch}
              handleCancel={() => setIsOpenImageSearch(false)}
              handleImageSearchSave={handleImageSearchSave}
            />
          )
        }

        <HobnobModal
          style={{ display: 'flex', alignItem: 'center' }}
          containerStyles={{ margin: 'auto' }}
          show={isOpenToolTip}
          visibleButton
          cancelText={tooltipText.leftButton}
          confirmText={tooltipText.rightButton}
          onCancel={handleLeftButton}
          onConfirm={handleRightButton}
          onClose={() => setIsOpenToolTip(false)}
        >
          <div className={styles['tooltip-container']}>
            <div className={styles['title']}>Unsaved Changes</div>
            <div className={styles['subTitle']}>{tooltipText.title}</div>
          </div>
        </HobnobModal>
      </div>
      {(isLoading || isImageSearchLoading) && <LoadingComponent dark />}
    </DashboardContainer>
  )
}

export default CreateEventContainer
